import { ReportHeader } from '../../header/report-header';
import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { DataTablePageEvent } from 'primereact/datatable';
import { formatDate } from '../../../../utils/date-format';
import { InstallmentsSideBarFilter } from './side-bar/installments-side-bar-filter';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { getReportInstallmentsOperation } from './operations/get-report-installments-operation';
import { payAllInstallmentsOperation } from './operations/pay-all-installments-operation';
import {
  InstallmentReportRow,
  PaymentInstallmentStatus,
} from '../../../../api/types/reports/accountant/installment.type';
import { InstallmentsSideBarFilterType } from './side-bar/installments-side-bar-filter.type';
import { WarningDialog } from '../../../../component/ui/warning-dialog/warning-dialog';
import generateAndDownloadExcelFile from '../../../../component/excel/generate-and-download-excel-file';
import { toast } from 'react-toastify';
import { createInstallmentsExcelData } from './operations/create-installments-excel-data';
import { formatClearanceStatusCode } from './utils/format-clearance-status-code';

export const ReportInstallments = () => {
  const [data, setData] = useState<InstallmentReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [payAllLoading, setPayAllLoading] = useState(false);
  const [showPayAllWarning, setShowPayAllWarning] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState<InstallmentsSideBarFilterType>({
    branchIds: [],
    includeUnassignedBranches: true,
    startMonth: new Date(),
    endMonth: new Date(),
    paymentStatuses: [],
  });

  const fetchReportData = async (
    filters: InstallmentsSideBarFilterType,
    newOffset: number = offset,
    newLimit: number = limit,
  ) => {
    setFilters(filters);
    setOffset(newOffset);
    setLimit(newLimit);
    await getReportInstallmentsOperation(
      filters,
      newOffset,
      newLimit,
      setData,
      setTotal,
      setLoading,
    );
  };

  const getStatusSeverity = (
    status: PaymentInstallmentStatus,
  ): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case PaymentInstallmentStatus.PAID:
        return 'success';
      case PaymentInstallmentStatus.PENDING:
        return 'warning';
      case PaymentInstallmentStatus.FAILED:
      case PaymentInstallmentStatus.CANCELLED:
        return 'danger';
      default:
        return 'warning';
    }
  };

  const getStatusLabel = (status: PaymentInstallmentStatus): string => {
    switch (status) {
      case PaymentInstallmentStatus.PENDING:
        return 'ממתין לתשלום';
      case PaymentInstallmentStatus.PAID:
        return 'שולם';
      case PaymentInstallmentStatus.FAILED:
        return 'נכשל';
      case PaymentInstallmentStatus.CANCELLED:
        return 'בוטל';
      default:
        return 'לא ידוע';
    }
  };

  useEffect(() => {
    fetchReportData(filters);
  }, []);

  const handlePayAllClick = () => {
    setShowPayAllWarning(true);
  };

  const payAllWindow = useMemo(() => {
    if (!filters.startMonth) {
      return null;
    }

    const y = filters.startMonth.getFullYear();
    const m = filters.startMonth.getMonth();

    // Charge window is ±7 days around the 20th of the same month.
    // Use local dates at midday to avoid DST edge cases.
    const center = new Date(y, m, 20, 12, 0, 0, 0);
    const start = new Date(center);
    start.setDate(center.getDate() - 7);
    const end = new Date(center);
    end.setDate(center.getDate() + 7);

    return { start, end };
  }, [filters.startMonth]);

  const canPayAll = useMemo(() => {
    if (!payAllWindow) return false;
    const now = new Date();
    const today = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      12,
    );
    return today >= payAllWindow.start && today <= payAllWindow.end;
  }, [payAllWindow]);

  const payAllBlockedTooltip = useMemo(() => {
    if (!payAllWindow) return 'יש לבחור חודש כדי לגבות תשלומים';
    const startLabel = payAllWindow.start.toLocaleDateString('he-IL');
    const endLabel = payAllWindow.end.toLocaleDateString('he-IL');
    return `ניתן לבצע גבייה רק בין ${startLabel} ל-${endLabel}`;
  }, [payAllWindow]);

  const handlePayAllConfirm = async () => {
    const success = await payAllInstallmentsOperation(
      filters.startMonth,
      filters.endMonth,
      setPayAllLoading,
      () => {
        // Refresh the data after successful payment
        fetchReportData(filters, offset, limit);
      },
    );
    if (success) {
      setShowPayAllWarning(false);
    }
  };

  const handleExportToExcel = async () => {
    if (excelLoading || loading) return;

    if (!total || total <= 0) {
      toast.warn('אין נתונים לייצוא');
      return;
    }

    let allRows: InstallmentReportRow[] = [];
    await getReportInstallmentsOperation(
      filters,
      0,
      total,
      (rows) => {
        allRows = rows;
      },
      () => undefined,
      setExcelLoading,
    );

    if (allRows.length === 0) {
      toast.warn('אין נתונים לייצוא');
      return;
    }

    generateAndDownloadExcelFile(
      createInstallmentsExcelData(
        allRows,
        formatDate,
        getStatusLabel,
        formatClearanceStatusCode,
      ),
    );
  };

  const getSelectedMonthRangeLabel = (): string => {
    if (!filters.startMonth || !filters.endMonth) return '';
    const startLabel = filters.startMonth.toLocaleDateString('he-IL', {
      month: 'long',
      year: 'numeric',
    });
    const endLabel = filters.endMonth.toLocaleDateString('he-IL', {
      month: 'long',
      year: 'numeric',
    });
    if (startLabel === endLabel) {
      return startLabel;
    }
    return `${startLabel} עד ${endLabel}`;
  };

  const handleOnSearch = async (
    searchFilters: InstallmentsSideBarFilterType,
  ) => {
    await fetchReportData(searchFilters, 0, limit);
  };

  const onPageChange = (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    fetchReportData(filters, newOffset, newLimit);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח הוראות קבע באשראי"
        description="דו״ח זה מציג מידע אודות כלל הוראות הקבע באשראי של האירגון. הדו״ח מאפשר מעקב אחר תשלומים חודשיים לפי סטטוס ותאריך."
        metadata={[
          {
            label: 'מטרת הדו״ח',
            value: 'מעקב אחר הוראות קבע וסטטוס התשלומים החודשיים',
          },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="accounting"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <InstallmentsSideBarFilter onSearch={handleOnSearch} />
        </div>

        {/* Table Section */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {/* Action Button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ fontSize: '14px', color: '#4b5563' }}>
              סה״כ רשומות: {total.toLocaleString()}
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                style={{
                  height: '30px',
                  width: '30px',
                  backgroundColor: '#10b981',
                  borderColor: '#10b981',
                }}
                icon="pi pi-file-excel"
                tooltip="ייצוא לאקסל"
                loading={excelLoading}
                disabled={excelLoading || loading || total <= 0}
                onClick={handleExportToExcel}
              />

              <Button
                label="גבה את כל התשלומים"
                icon="pi pi-credit-card"
                className="p-button-success"
                loading={payAllLoading}
                outlined={true}
                style={{
                  height: '30px',
                }}
                disabled={
                  !filters.startMonth ||
                  !filters.endMonth ||
                  loading ||
                  !canPayAll
                }
                /*disabled={true}*/
                onClick={handlePayAllClick}
                tooltip={
                  !filters.startMonth || !filters.endMonth
                    ? 'יש לבחור טווח חודשים כדי לגבות תשלומים'
                    : !canPayAll
                      ? payAllBlockedTooltip
                      : 'לחץ לגביית כל התשלומים של טווח החודשים הנבחר'
                }
                tooltipOptions={{ position: 'top', showOnDisabled: true }}
              />
            </div>
          </div>

          <DataTable
            value={data}
            rowHover
            loading={loading}
            rowClassName={() => 'cursor-pointer'}
            paginator
            rows={limit}
            first={offset}
            totalRecords={total}
            onPage={onPageChange}
            lazy={true}
            rowsPerPageOptions={[10, 20, 50, 100]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="installmentPlanId"
            scrollable
            scrollHeight="calc(100vh - 230px)"
            tableStyle={{
              direction: 'rtl',
            }}
            className="installments-table"
          >
            <Column
              field="branchName"
              header="שלוחה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="firstName"
              header="שם משלם"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <label>
                  {rowData.firstName} {rowData.lastName}
                </label>
              )}
            />
            <Column
              field="totalAmount"
              header="סכום כולל"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => `₪${rowData.totalAmount?.toLocaleString()}`}
            />
            <Column
              field="numberOfInstallments"
              header="מספר תשלומים"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="installmentNumber"
              header="תשלום מספר"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) =>
                `${rowData.installmentNumber} / ${rowData.numberOfInstallments}`
              }
            />
            <Column
              field="amount"
              header="סכום תשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => `₪${rowData.amount?.toLocaleString()}`}
            />
            <Column
              field="dueDate"
              header="תאריך לתשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.dueDate)}
            />
            <Column
              field="paidAt"
              header="תאריך תשלום בפועל"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) =>
                rowData.paidAt ? formatDate(rowData.paidAt) : '-'
              }
            />
            <Column
              field="firstPaymentDate"
              header="תאריך תשלום ראשון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.firstPaymentDate)}
            />
            <Column
              field="paymentDayOfMonth"
              header="יום חיוב בחודש"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="status"
              header="סטטוס"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <Tag
                  style={{ minWidth: '100px' }}
                  value={getStatusLabel(rowData.status)}
                  severity={getStatusSeverity(rowData.status)}
                />
              )}
            />
            <Column
              field="clearanceStatusCode"
              header="סטטוס סליקה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) =>
                formatClearanceStatusCode(rowData.clearanceStatusCode)
              }
            />
          </DataTable>
        </div>
      </div>

      {/* Pay All Warning Dialog */}
      <WarningDialog
        header="גביית כל התשלומים"
        isVisible={showPayAllWarning}
        onHide={() => setShowPayAllWarning(false)}
        addIcon={true}
        content={`האם אתה בטוח שברצונך לגבות את כל התשלומים עבור ${getSelectedMonthRangeLabel()}?`}
        dangerousContent="פעולה זו תבצע חיוב בכרטיסי האשראי של כל הלקוחות עם הוראות קבע פעילות לטווח החודשים הנבחר."
        smallLetters="לאחר ביצוע הפעולה, לא ניתן יהיה לבטל את החיובים שבוצעו."
        notInformation="שים לב: הפעולה תגבה את כל התשלומים הממתינים בטווח החודשים הנבחר. ודא שבחרת את הטווח הנכון לפני האישור."
        onConfirm={handlePayAllConfirm}
        confirmLabel="אישור גבייה"
        confirmLoading={payAllLoading}
        cancelLabel="ביטול"
        disableApproveButton={!canPayAll}
        approveButtonToolTip={payAllBlockedTooltip}
      />
    </div>
  );
};
